// wav-encoder.js
//
// Encoder mínimo de Float32 PCM → WAV 16-bit PCM mono.
// Sin dependencias externas (faster-whisper-server acepta WAV directo).
//
// Uso:
//   import { float32ToWav, concatFloat32 } from '@/util/wav-encoder'
//   const blob = float32ToWav(concatFloat32(samples), 16000)
//   // → POST como multipart/form-data al servicio STT
//
// Formato del WAV producido:
//   - RIFF header (12 bytes)
//   - fmt chunk (24 bytes): PCM uncompressed, mono, 16-bit
//   - data chunk: samples PCM 16-bit little-endian
//
// Total = 44 bytes header + 2 * samples.length

// Concatena varios Float32Array en uno solo. Útil para juntar los chunks
// que el AudioWorklet va posteando antes de encodear el burst entero.
export function concatFloat32(chunks) {
  let total = 0
  for (let i = 0; i < chunks.length; i++) total += chunks[i].length
  const out = new Float32Array(total)
  let offset = 0
  for (let i = 0; i < chunks.length; i++) {
    out.set(chunks[i], offset)
    offset += chunks[i].length
  }
  return out
}

// Encodea un Float32Array a un Blob WAV.
// sampleRate suele ser 16000 (lo que espera Whisper). Float32 in [-1,1]
// → Int16 [-32768, 32767]. Clamp defensivo por si el AudioContext entrega
// muestras fuera de rango (raro pero posible con AGC del browser).
export function float32ToWav(samples, sampleRate) {
  const numSamples = samples.length
  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)

  // RIFF header
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + numSamples * 2, true) // chunk size
  writeString(view, 8, 'WAVE')

  // fmt chunk
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)            // subchunk size (16 = PCM)
  view.setUint16(20, 1, true)             // audio format (1 = PCM)
  view.setUint16(22, 1, true)             // num channels (1 = mono)
  view.setUint32(24, sampleRate, true)    // sample rate
  view.setUint32(28, sampleRate * 2, true) // byte rate (sampleRate * blockAlign)
  view.setUint16(32, 2, true)             // block align (numChannels * bitsPerSample/8)
  view.setUint16(34, 16, true)            // bits per sample

  // data chunk
  writeString(view, 36, 'data')
  view.setUint32(40, numSamples * 2, true) // subchunk size

  // PCM 16-bit little-endian
  let offset = 44
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
    offset += 2
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

// RMS de un Float32Array. Útil para VAD client-side: si rms > umbral
// (~0.02 para voz normal a -34dBFS), hay actividad de speech. Si está
// debajo del umbral durante N ms consecutivos, asumimos silencio y
// terminamos el burst para enviar al STT.
export function rmsOf(samples) {
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
  return Math.sqrt(sum / samples.length)
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
}

export default { float32ToWav, concatFloat32, rmsOf }
